import * as React from 'react';

import { Formatters, Messages } from '../../../codegen/Localize';
import { TooltipDraftIndicator } from '../../../drafts/components/TooltipDraftIndicator';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../ts/models/IDataSourceInfo';
import { IWorkbookInfo } from '../../../ts/models/IWorkbookInfo';
import { DataSources } from '../../../content/resources/Datasources';
import { DraftType } from '../../../ts/ServerApi/types/IDraft';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { getCommonDatasourceInfo } from '../CommonDatasourceInfo';
import { MarkupField } from '../Components/MarkupField';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BasicField } from './Elements/BasicField';
import { EntityLinkProps } from './Elements/EntityLink';
import { Name } from './Elements/Name';
import { RelatedEntity } from './Elements/RelatedEntity';
import { RelatedEntityList } from './Elements/RelatedEntityList';
import { Tags } from './Elements/Tags';
import { Timestamp } from './Elements/Timestamp';
import { Tooltip } from './Elements/Tooltip';
import { User } from './Elements/User';
import { LocationField } from './Elements/LocationField';
import {
  ContentTooltipProps,
  getProjectLink,
  handleContinueEditingAction,
  TooltipDescriptionTestId,
} from './TooltipHelper';

export function WorkbookTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const relatedDatasources = getRelatedDatasourceLinkProps(props.content, props.urls);
  const numSheets = (content as IWorkbookInfo).numSheets;
  const projectLink = getProjectLink((content as IWorkbookInfo).project, urls);
  const locationType = (content as IWorkbookInfo).location?.type;
  return (
    <Tooltip>
      <Name name={content.name} />
      <MarkupField markup={props.content.description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <BasicField name={Messages.sheets()} value={numSheets} />
      <RelatedEntityList label={Messages.column_numDatasources()} list={relatedDatasources} />
      {!FeatureFlags.personalSpaceEnabled && !props.inProjectSpace && <RelatedEntity label={Messages.project()} link={projectLink} /> }
      {FeatureFlags.personalSpaceEnabled && <LocationField inProjectSpace={props.inProjectSpace} inPersonalSpace={props.inPersonalSpace} locationType={locationType} projectLink={projectLink}/>}
      <User user={props.content.owner} label={Messages.owner()} urls={urls} />
      <Timestamp label={Messages.modified()} value={props.content.updatedAt} />
      { FeatureFlags.modifiedByUserEnabled && props.content.modifiedBy !== undefined && <User user={props.content.modifiedBy} label={Messages.column_modified_by()} urls={urls} /> }
      {props.draftLastUpdatedTimeStamp && renderDraftIndicator(props.draftLastUpdatedTimeStamp, content)}
    </Tooltip>
  );
}

function getRelatedDatasourceLinkProps(workbook: IContentInfo, urls: SpacesSiteUrls): EntityLinkProps[] {
  if (workbook && workbook.hasOwnProperty('datasources')) {
    const datasources: IDataSourceInfo[] = (workbook as any).datasources;
    return datasources.map(ds => {
      const datasourceDetail = getCommonDatasourceInfo(ds);
      return {
        name: ds.name,
        href: urls.workbookDatasourcesUrl(workbook.id),
        iconImagePath: DataSources.getIconForDatasourceType(datasourceDetail, 'tooltip')
      };
    });
  } else {
    return [];
  }
}

export function renderDraftIndicator(draftLastUpdatedAt: string, workbook: IContentInfo): JSX.Element {
  const LAST_EDITED: string = Formatters.formatLongBestFitRelativeTime(new Date(draftLastUpdatedAt));
  return (
    <TooltipDraftIndicator
      indicatorText={() => Messages.draftLastEditIndicatorText({LAST_EDITED})}
      linkText={Messages.continueEditing}
      handleAction={handleContinueEditingAction(workbook)}
    />
  );
}
